/**
 * @file     ListingViewRecorder.tsx
 * @purpose  Fire-and-forget client component that records the current listing
 *           in the localStorage view set when the page was reached through
 *           Relta browsing. Direct and external arrivals do not advance the
 *           gate count.
 *           Render anywhere inside the listing detail tree.
 */
'use client';

import { useEffect } from 'react';
import { useViewedListings } from '@/hooks/useViewedListings';
import {
  LISTING_ENTRY_EVENT,
  resolveListingEntry,
} from '@/services/auth/listingEntrySource';

interface Props {
  listingKey: string | null | undefined;
}

export const ListingViewRecorder = ({ listingKey }: Props) => {
  const { record } = useViewedListings();

  useEffect(() => {
    if (!listingKey) return;
    const pathname = window.location.pathname;
    let counted = false;
    const recordIfInternal = (internal: boolean) => {
      if (internal && !counted) {
        counted = true;
        record(listingKey);
      }
    };
    const update = (event: Event) => {
      const detail = (event as CustomEvent<{ pathname: string; internal: boolean }>).detail;
      if (detail?.pathname === pathname) recordIfInternal(detail.internal);
    };
    window.addEventListener(LISTING_ENTRY_EVENT, update);
    recordIfInternal(resolveListingEntry(pathname));
    return () => window.removeEventListener(LISTING_ENTRY_EVENT, update);
  }, [listingKey, record]);

  return null;
};
